// src/components/report/ProblemSolutionCard.jsx
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  AlertTriangle, Lightbulb, Zap, ChevronDown, ChevronUp,
  BarChart, DollarSign, Users, Target, Clock, ExternalLink,
} from 'lucide-react';
import { formatCurrency, severityStyles } from '../../utils/reportHelpers';

// Scale a problem's median_loss_per_month for the buyer's stated business
// size so a 1-5-person store doesn't see the same monthly-loss claim as a
// medium-sized chain. Same scaling we use on the audit live counter to keep
// the dollar figures consistent across the funnel. Caller passes the size
// string ("solo" / "small" / "medium" / "large"); unknown defaults to 0.5.
const SIZE_SCALE = { solo: 0.20, small: 0.35, medium: 0.70, large: 1.0 };
const _scaleLoss = (rawMonthly, businessSize) => {
  const scale = SIZE_SCALE[(businessSize || '').trim()] ?? 0.5;
  return Math.round((rawMonthly || 0) * scale);
};

export const ProblemSolutionCard = ({ problem, index, onGoToTool = null, toolUrl = null, toolStatus = 'notstarted', businessSize = '' }) => {
  const [expanded, setExpanded] = useState(false);
  const s = severityStyles[problem.severity] ?? severityStyles.LOW;
  // Round-14 (Sarah R13 verdict): prefer the backend-stamped canonical
  // share (each chip's slice of cost_of_inaction_12m / 12) over the raw
  // median × size-scale. This makes the per-pain card $/mo agree with
  // the hero figure, the cost-of-inaction range, and the ROI tab —
  // closing the 5-number sprawl Sarah's reviewer flagged. Falls back
  // to the legacy local scale when the canonical isn't stamped
  // (legacy reports / Discover path / no chips submitted).
  const _displayedLoss = problem.canonical_loss_per_month
    || _scaleLoss(problem.median_loss_per_month, businessSize);

  const tool = problem.recommended_tool || null;

  const pricingDisplay = tool?.starting_price
    ? typeof tool.starting_price === 'number'
      ? `${formatCurrency(tool.starting_price)}/mo`
      : tool.starting_price
    : tool?.pricing_band || 'Pricing varies';

  const deployTime =
    tool?.typical_setup_hours != null
      ? `${tool.typical_setup_hours} hrs setup`
      : '30–60 min';

  const integrationComplexity =
    tool?.integrationComplexity || tool?.implementation_complexity_score || 'Easy';

  const hasExtraInsights =
    problem.observed_prevalence ||
    _displayedLoss > 0 ||
    (problem.root_causes && problem.root_causes.length > 0) ||
    (problem.typical_manifestations && problem.typical_manifestations.length > 0);

  const setupSteps = Array.isArray(tool?.setupSteps) ? tool.setupSteps.slice(0, 3) : [];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.06 }}
      className={`rounded-2xl border-l-4 ${s.border} ${s.bg} bg-slate-900 overflow-hidden${toolStatus === 'done' ? ' opacity-60' : ''}`}
    >
      <div className="p-6">
        {/* Problem header */}
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${s.icon}`} />
            <div>
              {/* Round-15 (Sarah R14 verdict): prefer the buyer's literal
                  chip text over the catalog node's problem_text. The
                  chip→node resolver can match a chip to the wrong
                  catalog node when typical_manifestations contain
                  buyer-keyword phrases (e.g. "Writing product
                  descriptions" hit retail_seo_organic_01 because its
                  manifestation includes "Product descriptions
                  copy-pasted from manufacturer"). Plan/Stack/ROI/Prompts
                  all quote the chip; Diagnosis was rendering catalog
                  text. Now Diagnosis quotes the chip too — same source
                  as every other surface in the report. */}
              <p className="font-semibold text-white text-lg leading-snug">
                {problem.buyer_chip_text || problem.problem}
              </p>
              {problem.category && (
                <Badge className="mt-1 bg-white/8 text-slate-300 text-xs border border-white/8">
                  {/* R31 (Sarah R31 verdict): humanize cluster slugs.
                      R31.3 (Sarah R32 verdict): expanded the humanizer
                      so 2-letter acronyms render correctly. The naive
                      \b\w title-caser left "hr" → "Hr" — a half-formatted
                      slug Sarah caught next to "Client retention low".
                      ACRONYMS list mirrors the ToolsTab one so
                      categories like `hr`, `kpi`, `ai`, `crm`, `pos`
                      render uppercase. Generic underscored slugs still
                      get title-case. Plan vs Problems tabs stay
                      consistent with the R30 Plan-card humanizer. */}
                  {(() => {
                    const ACRONYMS = new Set([
                      'AI', 'API', 'CRM', 'MLS', 'POS', 'EHR', 'KPI',
                      'ROI', 'SEO', 'CMS', 'CFO', 'COO', 'CMO', 'CPA',
                      'IT', 'QA', 'AR', 'VR', 'UX', 'UI', 'IP', 'QR',
                      'SMB', 'ML', 'NPS', 'PR', 'HR', 'GA', 'GTM',
                      'B2B', 'B2C', 'CSV', 'PDF', 'SQL', 'URL', 'OS',
                      'IOT',
                    ]);
                    return String(problem.category)
                      .replace(/[_-]+/g, ' ')
                      .split(' ')
                      .map((w) => {
                        if (!w) return w;
                        const upper = w.toUpperCase();
                        if (ACRONYMS.has(upper)) return upper;
                        return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
                      })
                      .join(' ');
                  })()}
                </Badge>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {problem.match_score && (
              <Badge className="bg-blue-500/20 text-[#3b82f6] text-xs border border-blue-500/30">
                ✓ Confirmed
              </Badge>
            )}
            {toolStatus === 'done' && (
              <Badge className="bg-blue-500/20 text-[#3b82f6] text-xs border border-blue-500/30">
                ✓ Fixed
              </Badge>
            )}
            {toolStatus === 'inprogress' && (
              <Badge className="bg-blue-500/20 text-blue-300 text-xs border border-blue-500/30">
                ⚡ In Progress
              </Badge>
            )}
            <Badge className={s.badge}>{problem.severity}</Badge>
          </div>
        </div>

        {/* Solution pattern */}
        {problem.solution_pattern && (
          <div className="ml-8 mb-4 bg-slate-950/50 rounded-xl p-4 border border-white/8">
            <div className="flex items-center gap-2 text-[#3b82f6] text-sm font-semibold mb-1">
              <Lightbulb className="w-4 h-4" /> Solution Approach
            </div>
            <p className="text-slate-300 text-sm">{problem.solution_pattern}</p>
          </div>
        )}

        {/* Recommended tool */}
        {tool && (
          <div className="ml-8 bg-blue-900/20 border border-blue-500/30 rounded-xl p-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Zap className="w-4 h-4 text-[#3b82f6]" />
                </div>
                <div>
                  <div className="font-semibold text-white">
                    {tool.tool_name || tool.name}
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5">
                    {pricingDisplay} &bull; {integrationComplexity} setup &bull;{' '}
                    {deployTime}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setExpanded((e) => !e)}
                  className="text-[#3b82f6] hover:text-blue-300 text-xs"
                >
                  {expanded ? 'Hide details' : 'Why this tool?'}
                  {expanded ? (
                    <ChevronUp className="ml-1 w-3 h-3" />
                  ) : (
                    <ChevronDown className="ml-1 w-3 h-3" />
                  )}
                </Button>
                {(onGoToTool || toolUrl) && (
                  <a
                    href={toolUrl || '#'}
                    target={toolUrl ? '_blank' : undefined}
                    rel="noopener noreferrer"
                    onClick={(e) => {
                      if (onGoToTool) { e.preventDefault(); onGoToTool(tool.tool_name || tool.name); }
                    }}
                    className="inline-flex items-center gap-1.5 text-xs bg-blue-500/20 hover:bg-blue-500/30 text-[#3b82f6] border border-blue-500/30 hover:border-blue-400/50 rounded-full px-3 py-1.5 transition-colors font-medium"
                  >
                    <Zap className="w-3 h-3" />
                    Start fixing this →
                  </a>
                )}
              </div>
            </div>
            <AnimatePresence>
              {expanded && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-3 pt-3 border-t border-blue-500/20 text-sm text-slate-300 space-y-3"
                >
                  {/* ROI recovery dollar amount intentionally not duplicated
                      here — the always-visible card footer already shows
                      "$X/mo from this problem alone" with the same number.
                      Showing it again inside the expanded "Why this tool"
                      panel was the duplication QA flagged as padding. */}
                  {/* Why matched */}
                  {tool.matchReason && (
                    <p className="italic text-slate-400">
                      “{tool.matchReason}”
                    </p>
                  )}
                  {problem.why_matched && !tool.matchReason && (
                    <p className="italic text-slate-400">
                      “{problem.why_matched}”
                    </p>
                  )}

                  {/* Setup steps */}
                  {setupSteps.length > 0 && (
                    <div>
                      <p className="font-semibold text-[#3b82f6] text-xs">
                        Setup steps:
                      </p>
                      <ol className="space-y-1 mt-1">
                        {setupSteps.map((step, idx) => (
                          <li
                            key={`${tool.id || tool.tool_name}-step-${idx}`}
                            className="flex gap-2"
                          >
                            <span className="text-[#3b82f6] font-mono">
                              {idx + 1}.
                            </span>
                            {step}
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}

                  {/* Success story */}
                  {tool.successStory && (
                    <div className="bg-slate-950/50 p-3 rounded-xl">
                      <p className="font-semibold text-[#3b82f6] text-xs">
                        Success story:
                      </p>
                      <p className="text-sm mt-1">{tool.successStory}</p>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* Benchmark */}
        {problem.benchmark && (
          <div className="ml-8 mt-3 text-xs text-slate-500 flex items-center gap-1">
            <BarChart className="w-3 h-3" /> {problem.benchmark}
          </div>
        )}

        {/* Extra insights */}
        {hasExtraInsights && (
          <div className="mt-4 flex flex-wrap gap-3 text-xs text-slate-500 border-t border-white/8 pt-3">
            {problem.observed_prevalence >= 50 && (
              <span className="flex items-center gap-1 text-purple-400">
                <Users className="w-3 h-3" /> Common in {Math.round(problem.observed_prevalence)}% of similar businesses
              </span>
            )}
            {problem.observed_prevalence >= 25 && problem.observed_prevalence < 50 && (
              <span className="flex items-center gap-1 text-purple-400">
                <Users className="w-3 h-3" /> Seen in roughly {Math.round(problem.observed_prevalence)}% of similar businesses
              </span>
            )}
            {_displayedLoss > 0 && (
              <span className="flex items-center gap-1 text-amber-400">
                <DollarSign className="w-3 h-3" />
                ~{formatCurrency(_displayedLoss)}/mo industry-typical loss for this problem at your size
              </span>
            )}
            {problem.typical_manifestations?.length > 0 && (
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" /> Signs:{' '}
                {problem.typical_manifestations.slice(0, 2).join(', ')}
              </span>
            )}
            {problem.root_causes?.length > 0 && (
              <span className="flex items-center gap-1">
                <Target className="w-3 h-3" /> Root cause:{' '}
                {problem.root_causes[0]}
              </span>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
};
